import {
  builderStoreAtom,
  formSettingAtom,
  interpreterStoreAtom,
  registryStoreAtom,
} from "@/store";
import { FormSchema } from "@/types/domain";
import { Validator } from "@/types/validator";
import { useAtom, useStore } from "jotai";
import { useCodeHelper } from "./useCode/useCodeHelper";
import { useValidateFields } from "./useValidator";
import {
  getComponentValidator,
  getRegistryValidators,
  getValidatorRequired,
} from "@/components/form/attributes/validator/definition";

export default function useValidatorStore() {
  const [registryStore, setRegistryStore] = useAtom(registryStoreAtom);
  const [{ validation: formValidators }] = useAtom(formSettingAtom);
  const [bStore] = useAtom(builderStoreAtom);
  const store = useStore();
  const iStore = store.get(interpreterStoreAtom);
  const { mutateAsync: validateApplicationFields } = useValidateFields();

  const schemaForCodeHelper = (iStore?.schema as FormSchema | undefined) ??
    bStore?.getSchema() ?? { root: [], entities: {} };

  const { executeValidator } = useCodeHelper({
    formSchema: schemaForCodeHelper,
    formData: iStore?.getData() ?? {},
    validateFields: validateApplicationFields,
  });

  async function initiateRegistryStore(_schema: FormSchema) {
    setRegistryStore({});
  }

  function removeValidator(entityId: string) {
    setRegistryStore((prev) => {
      const next = { ...prev };
      delete next[entityId];
      return next;
    });
  }

  function addValidator(entityId: string, validator: Validator) {
    setRegistryStore((prev) => ({
      ...prev,
      [entityId]: [...(prev[entityId] ?? []), validator],
    }));
  }

  function updateValidator(entityId: string, validator: Validator) {
    setRegistryStore((prev) => ({
      ...prev,
      [entityId]: (prev[entityId] ?? []).map((item) =>
        item.id === validator.id ? validator : item,
      ),
    }));
  }

  function getValidator(entityId: string): Validator | undefined {
    return registryStore[entityId]?.[0];
  }

  function getValidators(entityId: string): Validator[] {
    return registryStore[entityId] ?? [];
  }

  function setValidators(entityId: string, validators: Validator[]) {
    setRegistryStore((prev) => ({
      ...prev,
      [entityId]: validators,
    }));
  }

  function hasValidator(entityId: string) {
    return (registryStore[entityId]?.length ?? 0) > 0;
  }

  function getFieldNameByEntityId(entityId: string): string {
    const latestSchema =
      (store.get(interpreterStoreAtom)?.schema as FormSchema | undefined) ??
      bStore?.getSchema();
    console.debug({ latestSchema });
    const rawName = (
      latestSchema?.entities?.[entityId]?.attributes as
        | Record<string, unknown>
        | undefined
    )?.name;
    return typeof rawName === "string" && rawName.trim() !== ""
      ? rawName.trim()
      : entityId;
  }

  async function executeRegistryValidator({
    entityId,
    value,
    required = false,
    registryValidatorConfigs = [],
  }: {
    entityId: string;
    value: unknown;
    required: boolean | undefined;
    registryValidatorConfigs?: ReturnType<typeof getRegistryValidators>;
  }): Promise<string | undefined> {
    const registryIds = registryValidatorConfigs
      .map(({ validatorId }) => validatorId.trim())
      .filter((validatorId) => validatorId !== "");
    if (registryIds.length === 0 || !required) {
      console.debug(entityId, value, "no registry");
      return undefined;
    }

    const validationResult = await executeValidator({
      value,
      isApi: true,
      currentField: getFieldNameByEntityId(entityId),
      formData: store.get(interpreterStoreAtom)?.getEntitiesValues?.() ?? {},
      registryIds,
      defaultErrorMessage: "Validation failed",
    });
    if (
      typeof validationResult === "object" &&
      validationResult.isValid === false
    ) {
      return validationResult.error ?? "Validation failed";
    }
    return undefined;
  }
  async function executeAllRegistry(
    formData: Record<string, unknown>,
    formSchema: FormSchema,
  ) {
    const errors: Record<string, string> = {};

    console.debug({ formData, formSchema });

    for (const [entityId, value] of Object.entries(formData)) {
      try {
        const attributes = formSchema?.entities[entityId].attributes as
          | Record<string, unknown>
          | undefined;
        const validatorAttr =
          attributes && "validator" in attributes
            ? attributes["validator"]
            : undefined;
        const registryValidatorConfigs = getRegistryValidators(validatorAttr);

        // console.debug({ formSchema }, formSchema.entities[entityId]);

        const error = await executeRegistryValidator({
          entityId,
          value,
          required: getValidatorRequired(validatorAttr),
          registryValidatorConfigs,
        });

        if (error) {
          errors[entityId] = error;
        }
      } catch (e) {
        errors[entityId] = `Validator execution error: ${String(e)}`;
      }
    }
    console.debug({ errors });
    return errors;
  }
  async function executeLocalValidator({
    entityId,
    validatorCode,
    defaultErrorMessage,
    value,
    required = false,
    isApi,
  }: {
    entityId: string;
    validatorCode?: string;
    defaultErrorMessage?: string;
    value: unknown;
    required: boolean | undefined;
    isApi?: boolean;
  }): Promise<string | undefined> {
    if (!validatorCode || !required) return undefined;

    const validationResult = await executeValidator({
      validatorCode,
      value,
      isApi,
      currentField: getFieldNameByEntityId(entityId),
      formData: store.get(interpreterStoreAtom)?.getEntitiesValues?.() ?? {},
      defaultErrorMessage: defaultErrorMessage ?? "Validation failed",
    });

    console.debug({ validationResult });
    if (
      typeof validationResult === "object" &&
      validationResult.isValid === false
    ) {
      return validationResult.error ?? defaultErrorMessage;
    } else if (validationResult === false) {
      return defaultErrorMessage;
    }
    return undefined;
  }
  async function executeAllLocalValidator(
    formData: Record<string, unknown>,
  ): Promise<Record<string, string>> {
    const schema = store.get(interpreterStoreAtom)?.schema as
      | FormSchema
      | undefined;
    // const schema = bStore?.getSchema();
    if (!schema?.entities) return {};
    const errors: Record<string, string> = {};
    for (const [entityId, entity] of Object.entries(schema.entities)) {
      const validatorAttr =
        (
          entity.attributes as
            | {
                validator?: {
                  required: boolean;
                  componentValidator?: {
                    code?: string;
                    errorMessage?: string;
                    enableApi?: boolean;
                  };
                  code?: string;
                  isApi?: boolean;
                };
              }
            | undefined
        )?.validator ?? undefined;
      const componentValidator = getComponentValidator(validatorAttr);
      const validatorCode = componentValidator?.code;
      if (!validatorCode) continue;
      try {
        const error = await executeLocalValidator({
          entityId,
          validatorCode,
          defaultErrorMessage:
            componentValidator?.errorMessage ?? "Validation failed",
          value: formData[entityId],
          required: getValidatorRequired(validatorAttr),
          isApi: componentValidator?.enableApi,
        });
        if (error) {
          errors[entityId] = error;
        }
      } catch (e) {
        errors[entityId] = `Validator execution error: ${String(e)}`;
      }
    }
    return errors;
  }
  async function executeFormValidator(): Promise<Record<string, string>> {
    console.debug({ formValidators });
    if (!formValidators.required) {
      return {};
    }

    const { validators } = formValidators;
    const errors: Record<string, string> = {};
    const formData =
      store.get(interpreterStoreAtom)?.getEntitiesValues?.() ?? {};

    for (const [index, validator] of validators.entries()) {
      if (!validator.code) continue;

      const result = await executeValidator({
        validatorCode: validator.code,
        value: undefined,
        isApi: Boolean(validator.isApi),
        currentField: validator.key,
        formData,
        defaultErrorMessage: validator.errorMessage ?? "Validation failed.",
      });

      if (typeof result === "boolean") {
        if (!result) {
          errors[validator.key ?? String(index)] =
            validator.errorMessage ?? "Validation failed.";
        }
      } else if (!result.isValid) {
        errors[validator.key ?? String(index)] =
          result.error ?? validator.errorMessage ?? "Validation failed.";
      }
    }

    return errors;
  }

  return {
    initiateValidatorStore: initiateRegistryStore,
    validatorStore: registryStore,
    addValidator,
    updateValidator,
    removeValidator,
    getValidator,
    getValidators,
    setValidators,
    hasValidator,
    executeRegistryValidator,
    executeAllRegistry,
    executeValidator,
    executeLocalValidator,
    executeAllLocalValidator,
    executeFormValidator,
  };
}
